import { FEATURE } from '@abiglobalhealth/go-types'
import { useEffect, useMemo, useState } from 'react'

import { Popup } from '@/components/Popup'
import { useConfig } from '@/hooks/useConfig'
import { useConsultation } from '@/hooks/useConsultation'

import { PhoneNumberPopup } from './PhonePopup'
import { PhonePopupError } from './PhonePopupError'
import { PhonePopupSuccess } from './PhonePopupSuccess'

const DEFAULT_POPUP_TIMEOUT = 6_000 // 6s
const RESULT_POPUP_TIMEOUT = 3_000
const POPUP_EXIT_DURATION = 500

export enum SUBMIT_STATUS {
  SUCCESS = 'success',
  ERROR = 'error',
  SUBMITTING = 'submitting'
}

interface PhoneNumberPopupWrapperProps {
  popupTimeout?: number
}

export const PhoneNumberPopupWrapper = ({ popupTimeout = DEFAULT_POPUP_TIMEOUT }: PhoneNumberPopupWrapperProps) => {
  const { config } = useConfig()
  const { consultation } = useConsultation()

  const [showPopup, setShowPopup] = useState(false)
  const [hidePopup, setHidePopup] = useState(false)
  const [showInput, setShowInput] = useState(false)

  const [submitStatus, setSubmitStatus] = useState<SUBMIT_STATUS | null>(null)

  const shouldRender = useMemo(
    () => config?.features?.includes(FEATURE.ASK_PHONE_FOR_REMINDER) && !consultation?.notify,
    [config?.features, consultation?.notify]
  )

  useEffect(() => {
    if (!shouldRender) return

    const timeout = setTimeout(() => {
      setShowPopup(true)
    }, popupTimeout)

    return () => clearTimeout(timeout)
  }, [shouldRender])

  useEffect(() => {
    if (!submitStatus) return
    if (![SUBMIT_STATUS.SUCCESS, SUBMIT_STATUS.ERROR].includes(submitStatus)) return

    const hideTimeout = setTimeout(() => setHidePopup(true), RESULT_POPUP_TIMEOUT)
    const closeTimeout = setTimeout(() => setShowPopup(false), RESULT_POPUP_TIMEOUT + POPUP_EXIT_DURATION)

    return () => {
      clearTimeout(hideTimeout)
      clearTimeout(closeTimeout)
    }
  }, [submitStatus])

  const containerHeight = useMemo(() => {
    switch (submitStatus) {
      case SUBMIT_STATUS.SUCCESS:
      case SUBMIT_STATUS.ERROR:
        return '12rem'

      case SUBMIT_STATUS.SUBMITTING:
      case null:
        return showInput ? '14.5rem' : '7.5rem'
    }
  }, [submitStatus, showInput])

  const getPopupContent = () => {
    switch (submitStatus) {
      case SUBMIT_STATUS.SUCCESS:
        return <PhonePopupSuccess />

      case SUBMIT_STATUS.ERROR:
        return <PhonePopupError />

      case SUBMIT_STATUS.SUBMITTING:
      case null: {
        return <PhoneNumberPopup submitStatus={submitStatus} setSubmitStatus={setSubmitStatus} showInput={showInput} setShowInput={setShowInput} />
      }
    }
  }

  return (
    <>
      {(shouldRender || showPopup) && (
        <div
          className="sticky bottom-0 z-10 w-full shrink-0 overflow-hidden transition-[height] duration-500"
          data-testid="phone-number-popup"
          style={{
            height: showPopup ? containerHeight : 0
          }}
        >
          <Popup show={showPopup && !hidePopup} testId="phone-number-popup-inner">
            {getPopupContent()}
          </Popup>
        </div>
      )}
    </>
  )
}
